import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";
import { FORCE_INVALIDATE, removeFromList, updateModelWithOptions, withNestedInfiniteDataList } from "../../../utils";
import { extractRequestFromQueryKey } from "../../../keys";
import { applyMongoFilter } from "@tellescope/utils";
import type { InboxThread } from "@tellescope/types-models";

/**
 * Checks if a thread matches all filters in the request
 * @param thread - The thread to check
 * @param request - The load_threads request with filters
 * @returns true if thread matches ALL filters, false otherwise
 */
function threadMatchesAllFilters(
  thread: InboxThread,
  request?: {
    mdbFilter?: object;
    userIds?: string[];
    excludeIds?: string[];
    enduserIds?: string[];
    lastTimestamp?: Date;
  }
): boolean {
  if (!request) return true;

  // Check mdbFilter
  if (request.mdbFilter && !applyMongoFilter(thread, request.mdbFilter)) {
    return false;
  }

  // Check userIds filter - thread must be assigned to at least one of the specified users
  if (request.userIds && request.userIds.length > 0) {
    const hasMatchingUser = thread.userIds?.some((id) => request.userIds?.includes(id));
    if (!hasMatchingUser) return false;
  }

  // Check excludeIds filter - thread ID must NOT be in the exclusion list
  if (request.excludeIds?.includes(thread.id)) {
    return false;
  }

  // Check enduserIds filter - thread must be related to at least one of the specified endusers
  if (request.enduserIds && request.enduserIds.length > 0) {
    const hasMatchingEnduser = thread.enduserIds?.some((id) => request.enduserIds?.includes(id));
    if (!hasMatchingEnduser) return false;
  }

  // Check lastTimestamp filter - thread must not be newer than the timestamp boundary
  // Use the latest of timestamp and outboundTimestamp for comparison
  if (request.lastTimestamp) {
    const outboundTime = thread.outboundTimestamp instanceof Date ? thread.outboundTimestamp.getTime() : 0;
    const effectiveTimestamp = Math.max(thread.timestamp.getTime(), outboundTime);
    if (effectiveTimestamp > request.lastTimestamp.getTime()) {
      return false;
    }
  }

  // All filters passed
  return true;
}

/**
 * Determines what type of update operation should be performed based on:
 * - Whether the thread exists in the query data
 * - Whether the updated thread matches all filters
 *
 * @returns "upsert" | "remove" | "none"
 */
function determineUpdateOperation(threadExists: boolean, matchesFilters: boolean): "upsert" | "remove" | "none" {
  if (threadExists) {
    // Thread already in data
    if (matchesFilters) {
      return "upsert"; // Update in place
    } else {
      return "remove"; // Remove - no longer matches filters
    }
  } else {
    // Thread not in data
    if (matchesFilters) {
      return "upsert"; // Insert - now matches filters
    } else {
      return "none"; // Don't update - doesn't match filters
    }
  }
}

export const INBOX_THREADS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"inbox_threads"> =
  {
    updateOne: (mutationRequest, mutationResponse) => ({
      load_threads: {
        predicate: ({ state: { data }, queryKey }) => {
          const request = extractRequestFromQueryKey(queryKey);
          const threadId = mutationResponse?.id ?? mutationRequest.id;

          // If thread already exists in data, always update it
          const threadExists = !!data?.pages.some((page) => page.threads.some((thread) => thread.id === threadId));
          if (threadExists) return true;

          // If thread doesn't exist, check if it matches ALL filters
          if (!mutationResponse) return false;

          return threadMatchesAllFilters(mutationResponse, request);
        },
        exact: false,
        updater: (oldData, context) => {
          const request = context?.queryKey ? extractRequestFromQueryKey(context.queryKey) : undefined;
          if (request?.search) return FORCE_INVALIDATE;
          const threadId = mutationResponse?.id ?? mutationRequest.id;

          // Find existing thread in oldData
          let existingThread: NonNullable<typeof oldData>["pages"][number]["threads"][number] | undefined;
          for (const page of oldData?.pages ?? []) {
            existingThread = page.threads.find((t) => t.id === threadId);
            if (existingThread) break;
          }

          const { _options, ...updates } = mutationRequest;
          // Compute updated thread (response takes priority, fallback to merging request with existing)
          const updatedThread =
            mutationResponse ??
            (existingThread ? updateModelWithOptions(existingThread, updates, _options) : undefined);

          // If no updated thread, return oldData unchanged
          if (!updatedThread) {
            return oldData;
          }

          // Check if updated thread matches ALL filters
          const matchesFilters = threadMatchesAllFilters(updatedThread, request);

          // Determine what operation to perform
          const operation = determineUpdateOperation(!!existingThread, matchesFilters);

          // Execute the determined operation
          switch (operation) {
            case "upsert":
              // Update existing thread or insert new thread
              return withNestedInfiniteDataList({
                infiniteData: oldData,
                listKey: "threads",
                operation: {
                  type: "upsert",
                  args: {
                    matchFn: (item) => item.id === threadId,
                    updater: (thread) =>
                      mutationResponse ?? (thread ? updateModelWithOptions(thread, updates, _options) : undefined),
                    sortFn: (a, b) => {
                      const aTime = Math.max(
                        a.timestamp.getTime(),
                        a.outboundTimestamp instanceof Date ? a.outboundTimestamp.getTime() : 0
                      );
                      const bTime = Math.max(
                        b.timestamp.getTime(),
                        b.outboundTimestamp instanceof Date ? b.outboundTimestamp.getTime() : 0
                      );
                      return bTime - aTime;
                    },
                  },
                },
              });

            case "remove":
              // Remove thread that no longer matches filters
              return withNestedInfiniteDataList({
                infiniteData: oldData,
                listKey: "threads",
                operation: {
                  type: "remove",
                  args: {
                    matchFn: (item) => item.id === threadId,
                  },
                },
              });

            case "none":
              // No operation - thread doesn't match filters and isn't in data
              return oldData;
          }
        },
      },
    }),
    deleteOne: (deletedThreadId) => ({
      load_threads: {
        predicate: ({ state: { data } }) => {
          return !!data?.pages.some((page) => page.threads.some((thread) => thread.id === deletedThreadId));
        },
        exact: false,
        updater: (oldData) => {
          return {
            pages:
              oldData?.pages.map((page) => ({
                threads: removeFromList({
                  list: page.threads,
                  matchFn: (thread) => thread.id === deletedThreadId,
                }),
              })) ?? [],
            pageParams: oldData?.pageParams ?? [],
          };
        },
      },
    }),
    build_threads: (mutationRequest, mutationResponse) =>
      mutationResponse && !mutationResponse.alreadyBuilt
        ? {
            load_threads: {
              predicate: ({ queryKey }) => {
                const request = extractRequestFromQueryKey(queryKey);
                if (!request?.lastTimestamp) return true;
                return request.lastTimestamp.getTime() >= mutationRequest.from.getTime();
              },
              exact: false,
            },
            getByIds: {
              exact: false,
            },
            getSome: {
              exact: false,
            },
          }
        : {},
  } as const;
